import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Sentiment, Gender } from '../components/ClassificationCard/types';
import { useSelectorItems } from '@/core/hooks/useSelectorItems';

import type { PostMeta } from '@/core/services/interfaces/BulkAnnotationInterfaces';
export type { PostMeta } from '@/core/services/interfaces/BulkAnnotationInterfaces';
export type ItemState = 'add' | 'remove';
export type SelectionMap = Record<string, ItemState>;

interface MultipleEditionContextValue {
  isMultipleEditionOpen: boolean;
  selectedPostIds: Set<string>;
  availablePostIds: string[];
  openMultipleEdition: () => void;
  closeMultipleEdition: () => void;
  togglePostSelection: (postId: string) => void;
  clearSelection: () => void;
  setAvailablePostIds: (ids: string[]) => void;
  postsMetaMap: Map<string, PostMeta>;
  registerPostsMeta: (posts: Array<{ id: string } & PostMeta>) => void;
  selectAll: () => void;
  deselectAll: () => void;
  isSelectionLocked: boolean;
  lockSelection: () => void;
  unlockSelection: () => void;
  totalFilteredPostCount: number;
  setTotalFilteredPostCount: React.Dispatch<React.SetStateAction<number>>;
  allVisiblePostsAreSpam: boolean;
  groupsSelection: SelectionMap;
  themesSelection: SelectionMap;
  interestsSelection: SelectionMap;
  tagsSelection: SelectionMap;
  setGroupsSelection: React.Dispatch<React.SetStateAction<SelectionMap>>;
  setThemesSelection: React.Dispatch<React.SetStateAction<SelectionMap>>;
  setInterestsSelection: React.Dispatch<React.SetStateAction<SelectionMap>>;
  setTagsSelection: React.Dispatch<React.SetStateAction<SelectionMap>>;
  sentimentSelection: Sentiment | null;
  genderSelection: Gender | null;
  setSentimentSelection: React.Dispatch<React.SetStateAction<Sentiment | null>>;
  setGenderSelection: React.Dispatch<React.SetStateAction<Gender | null>>;
  checkedActive: boolean;
  setCheckedActive: React.Dispatch<React.SetStateAction<boolean>>;
  removeActive: boolean;
  setRemoveActive: React.Dispatch<React.SetStateAction<boolean>>;
  availableGroups: string[];
  availableThemes: string[];
  availableInterests: string[];
  availableTags: string[];
  loadingAvailableItems: boolean;
  refetchAvailableTags: () => Promise<void>;
  addTagsLocally: (tags: string[]) => void;
}

const MultipleEditionContext = createContext<MultipleEditionContextValue | null>(null);

export { MultipleEditionContext };

export const MultipleEditionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMultipleEditionOpen, setIsMultipleEditionOpen] = useState(false);
  const [selectedPostIds, setSelectedPostIds] = useState<Set<string>>(new Set());
  const [availablePostIds, setAvailablePostIds] = useState<string[]>([]);
  const [postsMetaMap, setPostsMetaMap] = useState<Map<string, PostMeta>>(new Map());
  const [groupsSelection, setGroupsSelection] = useState<SelectionMap>({});
  const [themesSelection, setThemesSelection] = useState<SelectionMap>({});
  const [interestsSelection, setInterestsSelection] = useState<SelectionMap>({});
  const [tagsSelection, setTagsSelection] = useState<SelectionMap>({});
  const [sentimentSelection, setSentimentSelection] = useState<Sentiment | null>(null);
  const [genderSelection, setGenderSelection] = useState<Gender | null>(null);
  const [checkedActive, setCheckedActive] = useState(false);
  const [removeActive, setRemoveActive] = useState(false);
  const [totalFilteredPostCount, setTotalFilteredPostCount] = useState(0);
  const [isSelectionLocked, setIsSelectionLocked] = useState(false);

  const {
    groups: availableGroups,
    themes: availableThemes,
    occupations: availableInterests,
    tags: availableTags,
    loading: loadingAvailableItems,
    addTagsLocally,
    refetchTags,
  } = useSelectorItems();

  const lockSelection = useCallback(() => setIsSelectionLocked(true), []);
  const unlockSelection = useCallback(() => setIsSelectionLocked(false), []);

  const openMultipleEdition = useCallback(() => setIsMultipleEditionOpen(true), []);

  const closeMultipleEdition = useCallback(() => setIsMultipleEditionOpen(false), []);

  const togglePostSelection = useCallback((postId: string) => {
    if (isSelectionLocked) return;
    setSelectedPostIds((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
      } else {
        next.add(postId);
      }
      return next;
    });
  }, [isSelectionLocked]);

  const clearSelection = useCallback(() => {
    setSelectedPostIds(new Set());
    setIsMultipleEditionOpen(false);
    setIsSelectionLocked(false);
  }, []);

  const selectAll = useCallback(() => {
    setSelectedPostIds(new Set(availablePostIds));
  }, [availablePostIds]);

  const deselectAll = useCallback(() => {
    setSelectedPostIds(new Set());
  }, []);

  const registerPostsMeta = useCallback((posts: Array<{ id: string } & PostMeta>) => {
    setPostsMetaMap(new Map(posts.map(({ id, channel, uid, polarity, gender, checked, spam }) => [
      id,
      { channel, uid, polarity, gender, checked, spam },
    ])));
  }, []);

  const allVisiblePostsAreSpam = useMemo(() => {
    if (availablePostIds.length === 0) return false;
    return availablePostIds.every((id) => postsMetaMap.get(id)?.spam === true);
  }, [availablePostIds, postsMetaMap]);

  const refetchAvailableTags = refetchTags;

  const contextValue = useMemo<MultipleEditionContextValue>(() => ({
    isMultipleEditionOpen,
    selectedPostIds,
    availablePostIds,
    openMultipleEdition,
    closeMultipleEdition,
    togglePostSelection,
    clearSelection,
    setAvailablePostIds,
    postsMetaMap,
    registerPostsMeta,
    selectAll,
    deselectAll,
    isSelectionLocked,
    lockSelection,
    unlockSelection,
    totalFilteredPostCount,
    setTotalFilteredPostCount,
    allVisiblePostsAreSpam,
    groupsSelection,
    themesSelection,
    interestsSelection,
    tagsSelection,
    setGroupsSelection,
    setThemesSelection,
    setInterestsSelection,
    setTagsSelection,
    sentimentSelection,
    genderSelection,
    setSentimentSelection,
    setGenderSelection,
    checkedActive,
    setCheckedActive,
    removeActive,
    setRemoveActive,
    availableGroups,
    availableThemes,
    availableInterests,
    availableTags,
    loadingAvailableItems,
    refetchAvailableTags,
    addTagsLocally,
  }), [
    isMultipleEditionOpen, selectedPostIds, availablePostIds,
    openMultipleEdition, closeMultipleEdition, togglePostSelection,
    clearSelection, postsMetaMap, registerPostsMeta,
    selectAll, deselectAll,
    isSelectionLocked, lockSelection, unlockSelection,
    groupsSelection, themesSelection, interestsSelection, tagsSelection,
    sentimentSelection, genderSelection,
    checkedActive, removeActive,
    totalFilteredPostCount,
    allVisiblePostsAreSpam,
    availableGroups, availableThemes, availableInterests, availableTags,
    loadingAvailableItems,
    refetchAvailableTags,
    addTagsLocally,
  ]);

  return (
    <MultipleEditionContext.Provider value={contextValue}>
      {children}
    </MultipleEditionContext.Provider>
  );
};

export const useMultipleEdition = (): MultipleEditionContextValue => {
  const ctx = useContext(MultipleEditionContext);
  if (!ctx) throw new Error('useMultipleEdition must be used within MultipleEditionProvider');
  return ctx;
};
